import type { AssignmentSuggestion } from '@chukua/common';

export type CandidateKind = 'truck' | 'trailer' | 'driver';

/**
 * The part of `POST /dispatch/eligibility` the picker reads. Declared here because the API's own types are not in `@chukua/common`; keep in
 * step with `eligibility-result.policy.ts` and `candidate-grouping.policy.ts` in the dispatch API module.
 */
export interface EligibilityCheck {
  check: string;
  status: 'pass' | 'warn' | 'block';
  code: string;
  reason: string | null;
  nonOverridable?: boolean;
}

export interface EligibilityCandidate {
  id: string;
  label: string;
  status: EligibilityCheck['status'];
  checks: EligibilityCheck[];
}

export interface EligibilityGroups {
  eligible: EligibilityCandidate[];
  warning: EligibilityCandidate[];
  blocked: EligibilityCandidate[];
}

export interface EligibilityResponse {
  trucks: EligibilityGroups;
  trailers: EligibilityGroups;
  drivers: EligibilityGroups;
}

export interface BlockedRow {
  key: string;
  kind: CandidateKind;
  id: string;
  label: string;
  /** Only the checks that block; the passing and warning ones are not why it is here. */
  failedChecks: EligibilityCheck[];
  /** A legal hard rule stands in the way: unlike any other block, no approver can override it (PRD → Controls). */
  isLegalBlock: boolean;
}

export interface RankedCrews {
  eligible: AssignmentSuggestion[];
  warning: AssignmentSuggestion[];
}

/** Ranked crews keep the server's order within each group. */
export function groupRankedCrews(crews: AssignmentSuggestion[]): RankedCrews {
  return {
    eligible: crews.filter((crew) => crew.verdict === 'pass'),
    warning: crews.filter((crew) => crew.verdict === 'warn'),
  };
}

const KINDS: { kind: CandidateKind; groups: (response: EligibilityResponse) => EligibilityGroups }[] = [
  { kind: 'truck', groups: (response) => response.trucks },
  { kind: 'trailer', groups: (response) => response.trailers },
  { kind: 'driver', groups: (response) => response.drivers },
];

function toBlockedRow(kind: CandidateKind, candidate: EligibilityCandidate): BlockedRow {
  const failedChecks = candidate.checks.filter((check) => check.status === 'block');
  return {
    key: `${kind}:${candidate.id}`,
    kind,
    id: candidate.id,
    label: candidate.label,
    failedChecks,
    isLegalBlock: failedChecks.some((check) => check.nonOverridable === true),
  };
}

/**
 * Every blocked truck, trailer and driver, as single units (the engine judges units, not crews). The suggestions and the eligibility
 * answer are two requests made moments apart, so a unit already in a ranked crew is left out rather than shown as both.
 */
export function blockedRows(response: EligibilityResponse, rankedOnPage: AssignmentSuggestion[]): BlockedRow[] {
  const ranked = new Set(rankedOnPage.flatMap((crew) => [`truck:${crew.truck.id}`, `trailer:${crew.trailer.id}`, `driver:${crew.driver.id}`]));
  return KINDS.flatMap(({ kind, groups }) => groups(response).blocked.map((candidate) => toBlockedRow(kind, candidate))).filter((row) => !ranked.has(row.key));
}

/**
 * `POST /dispatch/eligibility` has no search and is deliberately unpaged (like `GET /zones/map`), so the blocked rows are filtered here: the one
 * place the list rule (search on the server) cannot apply.
 */
export function filterBlockedRows(rows: BlockedRow[], q: string | undefined): BlockedRow[] {
  const term = q?.trim().toLowerCase();
  if (!term) return rows;
  return rows.filter((row) => row.label.toLowerCase().includes(term));
}
